/**
 * Reports page date-range picker — two DatePickers + preset buttons.
 * Sub-spec 08 Plan 2 §1.4.
 */
import { DatePicker } from '@/components/DatePicker';

export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;
}

interface Props {
  value: DateRange;
  onChange: (next: DateRange) => void;
}

function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  return isoDate(new Date());
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return isoDate(d);
}

function startOfMonthIso(offsetMonths: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offsetMonths);
  return isoDate(d);
}

function endOfMonthIso(offsetMonths: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offsetMonths + 1);
  d.setUTCDate(0); // last day of previous month = last day of target month
  return isoDate(d);
}

function startOfYearIso(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

const PRESETS: ReadonlyArray<{ label: string; build: () => DateRange }> = [
  { label: 'Last 7 days', build: () => ({ from: daysAgoIso(7), to: todayIso() }) },
  { label: 'Last 30 days', build: () => ({ from: daysAgoIso(30), to: todayIso() }) },
  { label: 'This month', build: () => ({ from: startOfMonthIso(0), to: todayIso() }) },
  {
    label: 'Last month',
    build: () => ({ from: startOfMonthIso(-1), to: endOfMonthIso(-1) }),
  },
  { label: 'Year to date', build: () => ({ from: startOfYearIso(), to: todayIso() }) },
];

export function DateRangePicker({ value, onChange }: Props) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <DatePicker
          label="From"
          value={value.from}
          onChange={(v) => onChange({ ...value, from: v })}
        />
        <DatePicker label="To" value={value.to} onChange={(v) => onChange({ ...value, to: v })} />
      </div>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onChange(p.build())}
            className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs text-muted-foreground press hover:bg-secondary hover:text-foreground"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function defaultDateRange(): DateRange {
  return { from: daysAgoIso(30), to: todayIso() };
}
